import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { AnyPrimitive } from '@/entities/common';
import { useAuthStore } from '../stores/auth.store';
import { useImpersonationStore } from '@/stores/impersonation.store';
import { isArray } from 'lodash';

export type FetchError = Error & {
  statusCode?: string;
  errorCode?: string;
};

// Render token injected by the report-render route so all API calls carry X-Render-Token
let _renderToken: string | null = null;

export const setGlobalRenderToken = (token: string): void => {
  _renderToken = token;
};

export const getGlobalRenderToken = (): string | null => _renderToken;

export const fetchUrl = async (url: string, options?: RequestInit): Promise<Response> => {
  const resp = await fetch(url, options);
  if (resp.ok) {
    return resp;
  }
  if (resp.status >= 400) {
    const respBody = await resp.json();
    let errorMsg: string;

    if (isArray(respBody?.message)) {
      errorMsg = 'Validation Failed';
    } else {
      errorMsg = respBody?.message || respBody?.error || resp.statusText;
    }

    console.error(`[fetch] ${options?.method || 'GET'} ${url} → ${resp.status}: ${errorMsg}`);

    const err: FetchError = new Error(errorMsg);
    err.statusCode = respBody?.statusCode || resp.status;
    err.errorCode = respBody?.errorCode;
    err.name = respBody?.name || respBody?.error;
    err.message = respBody?.message || respBody?.error || resp.statusText;

    throw err;
  }
  return resp;
};

export const fetchJson = async <T = unknown>(url: string, options?: RequestInit, toJson = true): Promise<T> => {
  const headers = omitBy(
    {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    isNil,
  );
  const response = await fetchUrl(url, {
    ...options,
    headers,
  });
  if (!toJson) {
    return response as T;
  }
  return response.json();
};

export const fetchJsonWithAccessToken = async <T = unknown>(
  url: string,
  accessToken: string,
  options?: RequestInit,
  signoutOnUnauthorized = false,
  toJson = true,
): Promise<T | undefined> => {
  const { signout } = useAuthStore();
  try {
    const renderToken = getGlobalRenderToken();
    const resp = await fetchJson<T>(
      url,
      {
        ...options,
        headers: {
          ...options?.headers,
          Authorization: `Bearer ${accessToken}`,
          ...(renderToken ? { 'X-Render-Token': renderToken } : {}),
        },
      },
      toJson,
    );
    return resp;
  } catch (error) {
    if (error.statusCode === 401 && signoutOnUnauthorized) {
      signout();
      return;
    }
    throw error;
  }
};

// Disale eslint rule because we don't know the shape of the response
export const fetchJsonWithAuthorization = async <T = unknown>(
  url: string,
  options?: RequestInit,
  toJson = true,
): Promise<T | undefined> => {
  const { getAccessToken } = useAuthStore();
  const { refreshAccessToken } = useAuthStore();
  try {
    const accessToken = getAccessToken();
    const resp = await fetchJsonWithAccessToken<T>(url, accessToken, options, false, toJson);
    return resp;
  } catch (error) {
    // If token is expired, refresh token and retry
    if (error.statusCode === 401) {
      // Refresh classifies the error internally: it signs out on a definitive
      // auth error (returns undefined) and throws if transient retries are
      // exhausted (propagates here, keeping the session).
      const newAccessToken = await refreshAccessToken();
      if (!newAccessToken) {
        return; // already signed out — nothing to retry
      }

      // Retry with the new access token; signout if it is still unauthorized.
      return fetchJsonWithAccessToken<T>(url, newAccessToken, options, true, toJson);
    }
    throw error;
  }
};

export const fetchJsonWithAuthorizationAndPersonation = async <T = unknown>(
  url: string,
  options?: RequestInit,
  toJson = true,
): Promise<T | undefined> => {
  const personationStore = useImpersonationStore();
  const { isImpersonating } = personationStore;
  if (!isImpersonating) {
    return fetchJsonWithAuthorization(url, options, toJson);
  }
  const optionsWithImpersonation = {
    ...options,
    headers: {
      ...options?.headers,
      'DH-IMPERSONATED-ID': personationStore.impersonatedUser.id,
    },
  };
  return fetchJsonWithAuthorization(url, optionsWithImpersonation, toJson);
};

export const buildQueryString = (params: Record<string, AnyPrimitive>): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      query.append(key, encodeURIComponent(value));
    }
  });
  // Decode query again to prevent URLSearchParams percent-encode strings
  return decodeURIComponent(query.toString());
};

export type SSEChunk = {
  data?: string;
  event?: string;
  error?: string;
  metadata?: {
    stop_reason?: string;
    usage?: {
      input_tokens: number;
      output_tokens: number;
      cache_read_tokens: number;
      cache_creation_tokens: number;
    };
    latency_ms?: number;
    cost_usd?: number;
  };
};

export async function* fetchSSEWithAuthorizationAndPersonation(
  url: string,
  options?: RequestInit,
  customHeaders?: Record<string, string>,
): AsyncGenerator<SSEChunk> {
  const personationStore = useImpersonationStore();

  const headersWithImpersonation = {
    ...customHeaders,
  };

  if (personationStore.isImpersonating) {
    headersWithImpersonation['DH-IMPERSONATED-ID'] = personationStore.impersonatedUser.id;
  }

  yield* fetchSSEWithAuthorization(url, options, headersWithImpersonation);
}

export async function* fetchSSEWithAuthorization(
  url: string,
  options?: RequestInit,
  customHeaders?: Record<string, string>,
): AsyncGenerator<SSEChunk> {
  const { getAccessToken, refreshAccessToken } = useAuthStore();

  let accessToken = getAccessToken();

  const makeRequest = async (token: string): Promise<Response> => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options?.headers,
        ...customHeaders,
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw { statusCode: 401 };
      }
      const error = await response.json().catch(() => ({ message: response.statusText }));
      const errorMessage: string = typeof error.message === 'string' ? error.message : 'SSE request failed';
      throw new Error(errorMessage);
    }

    return response;
  };

  let response: Response;
  try {
    response = await makeRequest(accessToken);
  } catch (error) {
    if (error.statusCode === 401) {
      accessToken = await refreshAccessToken();
      if (!accessToken) {
        return; // refresh signed out (definitive) — end the stream
      }
      response = await makeRequest(accessToken);
    } else {
      throw error;
    }
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (!trimmedLine) {
          continue;
        }

        if (trimmedLine.startsWith('data:')) {
          const data = trimmedLine.slice(5).trim();
          if (data === '[DONE]') {
            return;
          }
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              yield { error: parsed.error };
            } else if (parsed.type === 'metadata') {
              yield { metadata: parsed };
            } else {
              yield { data: parsed.content || parsed.text || data };
            }
          } catch {
            yield { data };
          }
        } else if (trimmedLine.startsWith('event:')) {
          yield { event: trimmedLine.slice(6).trim() };
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
